import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { PRIVACY_VERSION } from "@/pages/Privacy";

/**
 * Blocks the app for logged-in users whose consent is missing or stale.
 * Non-dismissable: they must Accept, read Privacy, or Sign out.
 */
export default function ConsentGate() {
  const { user, setUser, logout } = useAuth();
  const [busy, setBusy] = useState(false);

  const needsConsent = !!user && (!user.consent_accepted_at || user.privacy_version_accepted !== (user.privacy_version_current || PRIVACY_VERSION));
  if (!needsConsent) return null;

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/accept-privacy", { privacy_version: PRIVACY_VERSION });
      setUser({
        ...user,
        consent_accepted_at: data.consent_accepted_at,
        privacy_version_accepted: data.privacy_version_accepted,
        privacy_up_to_date: true,
      });
      toast.success("Thanks — consent recorded.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not record consent");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        data-testid="consent-gate-modal"
        className="max-w-lg [&>button.absolute]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B] inline-flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" /> Privacy Notice · v{PRIVACY_VERSION}
        </div>
        <h2 className="font-display font-black text-2xl mt-2">One quick thing before you continue.</h2>
        <p className="text-sm opacity-70">
          We've updated (or you haven't yet accepted) our Privacy Notice, which explains what personal data the club stores in this booking platform, why we use it, and your rights under UK GDPR.
        </p>
        <p className="text-sm opacity-70">
          Please review the full notice and confirm you're happy for us to continue.
        </p>
        <div className="flex flex-wrap items-center gap-3 pt-2">
          <a
            href="/privacy"
            target="_blank"
            rel="noreferrer"
            data-testid="consent-gate-read-link"
            className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded"
          >
            Read privacy notice
          </a>
          <button
            onClick={accept}
            disabled={busy}
            data-testid="consent-gate-accept-btn"
            className="text-[10px] tracking-widest uppercase px-4 py-2 rounded bg-[#83B68B] text-black hover:bg-[#83B68B] disabled:opacity-50"
          >
            {busy ? "Recording…" : "I agree — continue"}
          </button>
          <button
            onClick={logout}
            data-testid="consent-gate-signout-btn"
            className="text-[10px] tracking-widest uppercase px-3 py-1.5 opacity-70 hover:opacity-100 hover:text-red-400"
          >
            Sign out instead
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
